import React, { useCallback, useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import CheckIcon from '@mui/icons-material/Check'
import { alpha, useTheme } from '@mui/material/styles'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { partLifecycleGroupLabel, PART_LIFECYCLE_GROUPS } from '../partLifecycleGroup'
import { fetchParts, fetchPartTrack, fetchPartCounts, isOrderNotFoundError } from '../detailApi'
import PartsGrid, { GROUP_ORDER, groupColor } from './PartsGrid'
import PartTrackPanel, { SelectedPartCard, PART_QR_COLUMNS } from './PartTrackPanel'
import CancelledPartsSection from './CancelledPartsSection'
import PartsBatchPrintDialog from '../../../../generic/qr/labelPrinting/PartsBatchPrintDialog'
import { CONTROL_RADIUS } from '../detailControls'
import { EMPTY_DASH } from '../detailShared'

export { PART_QR_COLUMNS }

// §2.2 — el guion LARGO sustituye a los literales de ausencia en las celdas. Vive en una
// constante para que no puedan convivir dos caracteres distintos que a ojo no se distinguen.

const PAGE_SIZE = 20

// Una sola fila basta para que el servidor devuelva su `totalElements`: los sondeos de recuento
// piden la página más pequeña que existe, no la de 20 ni —muchísimo menos— todas las piezas.
const COUNT_PROBE_SIZE = 1
// «Print all labels» pages through the order in pages of this size (the server limit per page).
const PRINT_PAGE_SIZE = 500
// The search asks the server once the user stops typing, not once per key.
const SEARCH_DEBOUNCE_MS = 300

// §2.2/RN8 — el recuento se DECLARA. ⚠️⚠️ Y UN CERO NO ES UNA AUSENCIA: un grupo sin piezas
// enseña `0`, nunca un guion, porque «no hay ninguna rechazada» es información y «no he podido
// leerlo» es otra cosa. El guion queda para lo segundo (§8: un bloque que no carga pinta `—`).
//
// ⚠️ LA COMPARACIÓN ES CONTRA `''`, NO SOLO CONTRA NULO, y aquí está la trampa: la frontera
// (`detailApi.js`) normaliza las ausencias a CADENA VACÍA con `asEmptyString`, y `'' ?? '—'`
// devuelve `''` — el `??` queda defusado y el hueco se pinta en blanco. Es el defecto que tuvo
// cinco cifras de la cabecera pintando huecos durante meses.
const partCountText = (count) => (count === '' || count === null || count === undefined ? EMPTY_DASH : String(count))

// CU-L11/CU-L12 (T5b) — the parts grid (server-paged/searched/filtered, RN66/RN70) plus, on
// selecting a row, the part's sheet and operation track (RN67/RN68/RN69, N13).
const PartsBlock = ({ fabricationOrderId, onNotFound = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const [search, setSearch] = useState('')
  const [lifecycleGroup, setLifecycleGroup] = useState('')
  const [parts, setParts] = useState({ content: [], totalElements: 0, page: 0, size: PAGE_SIZE })
  // RN8: «loading» and «could not load» are not «no parts». Only a ready, empty answer is.
  const [partsStatus, setPartsStatus] = useState('loading')
  const [loadMoreFailed, setLoadMoreFailed] = useState(false)
  // The text of the box answers at once; the requests use the debounced value.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const partsRequest = useRef(0)
  const trackRequest = useRef(0)
  // `''` en un recuento significa «no se ha podido leer», nunca cero. Y `read` separa eso de
  // «todavía no ha llegado»: sin esa marca, mientras la consulta viaja se pintaría una fila entera
  // de guiones, o sea «no disponible» dicho de algo que sí está disponible y aún no ha llegado.
  const [partCounts, setPartCounts] = useState({ read: false, total: '', byGroup: {} })
  const [selectedPartId, setSelectedPartId] = useState(0)
  const [track, setTrack] = useState({ status: 'idle', data: {} })
  const [showBatchPrint, setShowBatchPrint] = useState(false) // historia 226, E9: print the labels of all the parts
  const [mountedComponentsExpanded, setMountedComponentsExpanded] = useState(false)
  // Plegadas por defecto: son 20 de 30 en la orden de referencia y ocupaban dos tercios de la
  // rejilla. Lo que se pliega es la LISTA — el recuento se sigue leyendo sin abrir nada.
  const [cancelledExpanded, setCancelledExpanded] = useState(false)
  // Cancelled parts are fetched on their own, when their button opens: the main list no longer
  // carries them (excludeCancelled), so they cannot be a client-side slice of its page any more.
  const EMPTY_CANCELLED = { content: [], totalElements: 0, page: 0, loaded: false }
  const [cancelledParts, setCancelledParts] = useState(EMPTY_CANCELLED)
  const [loadingMoreCancelled, setLoadingMoreCancelled] = useState(false)
  // Only the latest cancelled request may write: an older search/page answering late must not
  // replace or extend a newer list.
  const cancelledRequest = useRef(0)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])
  const loadCancelledParts = (page) => {
    const token = ++cancelledRequest.current
    if (page > 0) setLoadingMoreCancelled(true)
    return fetchParts(fabricationOrderId, { page, size: PAGE_SIZE, search: debouncedSearch, lifecycleGroup: 'CANCELLED' })
      .then((next) => {
        if (token !== cancelledRequest.current) return
        setCancelledParts((prev) => (page === 0 ? { ...next, loaded: true } : { ...next, loaded: true, content: [...prev.content, ...next.content] }))
      })
      .catch(() => {}) // keep what is on screen; the finally below clears the loading flag
      .finally(() => { if (token === cancelledRequest.current) setLoadingMoreCancelled(false) })
  }
  useEffect(() => {
    setCancelledParts(EMPTY_CANCELLED)
    if (cancelledExpanded) loadCancelledParts(0)
    else cancelledRequest.current++
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelledExpanded, fabricationOrderId, debouncedSearch])
  const [expandedGroups, setExpandedGroups] = useState([])
  const toggleGroup = (key) => setExpandedGroups((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]))

  // RN70/D20/P-7: page, search (by code) and the lifecycleGroup filter are ALWAYS sent to the
  // server — this effect never fetches more than one page to filter in the browser.
  const dropPendingParts = () => { partsRequest.current++ }
  const loadParts = () => {
    const token = ++partsRequest.current
    setPartsStatus('loading')
    setLoadMoreFailed(false)
    fetchParts(fabricationOrderId, { page: 0, size: PAGE_SIZE, search: debouncedSearch, lifecycleGroup, excludeCancelled: true })
      .then((next) => {
        if (token !== partsRequest.current) return
        setParts(next)
        setPartsStatus('ready')
      })
      .catch(() => { if (token === partsRequest.current) setPartsStatus('error') })
  }

  // A newer search, filter or order makes every older answer stale: the token drops it.
  useEffect(() => {
    loadParts()
    return dropPendingParts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fabricationOrderId, debouncedSearch, lifecycleGroup])

  const [loadingMore, setLoadingMore] = useState(false)
  // Shares the token of the search: a page of an old search must not be appended to a new list.
  const loadMoreParts = () => {
    const token = partsRequest.current
    setLoadingMore(true)
    setLoadMoreFailed(false)
    fetchParts(fabricationOrderId, { page: parts.page + 1, size: PAGE_SIZE, search: debouncedSearch, lifecycleGroup, excludeCancelled: true })
      .then((next) => {
        if (token !== partsRequest.current) return
        setParts((prev) => {
          const known = new Set(prev.content.map((part) => part.partId))
          return { ...next, content: [...prev.content, ...next.content.filter((part) => !known.has(part.partId))] }
        })
      })
      .catch(() => { if (token === partsRequest.current) setLoadMoreFailed(true) })
      .finally(() => { if (token === partsRequest.current) setLoadingMore(false) })
  }
  const shownParts = parts.content.length
  const hasMoreParts = shownParts < parts.totalElements

  // ⚠️⚠️ LOS RECUENTOS SALEN DEL `totalElements` DE CADA PÁGINA, JAMÁS DE LAS FILAS PINTADAS.
  // Contar `parts.content` sería lo cómodo y sería FALSO: el endpoint está paginado a 20, así que
  // una orden de 10 piezas daría el número correcto y una de 40 se quedaría corta — en silencio,
  // sin que nada en pantalla delate el error. Por eso cada grupo se pregunta por separado, con
  // `size=1`: lo que interesa de esa respuesta no son sus filas, es su total.
  //
  // 📌 No dependen de `search` ni de `lifecycleGroup` A PROPÓSITO: son la composición de LA ORDEN,
  // el denominador contra el que se lee la lista filtrada. Si se movieran con el filtro, elegir
  // «rechazadas» dejaría los otros cuatro a cero y el usuario perdería justo la referencia que ha
  // venido a buscar. De paso, escribir en el buscador sigue costando UNA sola consulta.
  //
  // 📌 El total se pregunta aparte en vez de sumar los cinco: si Sun publica un sexto grupo (y ya
  // ha publicado un quinto), la suma se quedaría corta sin avisar; el total del servidor no.
  //
  // 📌 UNA SOLA LLAMADA: Sun sirve la composición entera en `parts/counts`. Si esa ruta no existe (una
  // Sun anterior) o contesta otra cosa, se cae a los seis sondeos de siempre. Lo único que no cae a
  // sondeos es «la orden no existe»: eso es el 404 del diálogo.
  useEffect(() => {
    let discarded = false
    const readCount = (group) =>
      fetchParts(fabricationOrderId, { page: 0, size: COUNT_PROBE_SIZE, lifecycleGroup: group })
        .then((page) => page.totalElements)
        // Un recuento que no se puede leer NO es un cero: se queda ausente y se declara con `—`.
        .catch(() => '')
    const readByProbes = () =>
      Promise.all([readCount(''), ...PART_LIFECYCLE_GROUPS.map(readCount)]).then(([total, ...counts]) => {
        const byGroup = {}
        PART_LIFECYCLE_GROUPS.forEach((group, index) => { byGroup[group] = counts[index] })
        return { total, byGroup }
      })
    fetchPartCounts(fabricationOrderId)
      .catch((error) => {
        if (isOrderNotFoundError(error)) {
          onNotFound()
          return { total: '', byGroup: {} }
        }
        return readByProbes()
      })
      .then(({ total, byGroup }) => {
        if (discarded) return
        setPartCounts({ read: true, total, byGroup })
      })
    return () => { discarded = true }
  }, [fabricationOrderId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️⚠️ SE SELECCIONA LA PRIMERA, SIEMPRE. Y esto GENERALIZA la regla que ya había en vez de
  // ponerse a su lado, que es lo importante: la mejora 11 (pasada 33) seleccionaba sola cuando
  // había UNA pieza, con este argumento — «con dos o más no se toca: ahí elegir SÍ es una decisión
  // del usuario y adelantarla sería inventar cuál le interesa».
  //
  // 📌 EL PO REVOCA ESE ARGUMENTO, y queda escrito para que nadie lo reponga creyendo que se
  // perdió: al abrir, la tarjeta pedía un clic delante de una lista, y ese clic no decide nada que
  // el usuario no pueda deshacer eligiendo otra. Se adelanta la primera TAL COMO VENGA ORDENADA —
  // sin reordenar nada, que el orden es de Sun (RN34).
  //
  // ⚠️ Dos reglas de selección conviviendo es justo lo que acaba divergiendo, así que la de una
  // sola pieza NO se conserva aparte: queda subsumida —con una lista de una, la primera es esa—.
  //
  // El `selectedPartId !== 0` sigue evitando reseleccionar cuando el usuario ha cerrado el
  // recorrido a propósito: el efecto solo vuelve a correr si CAMBIA la lista, así que cerrarlo lo
  // deja cerrado hasta que se filtre o se traiga otra página.
  useEffect(() => {
    const rows = parts.content || []
    if (rows.length === 0) return
    if (selectedPartId === rows[0].partId) return
    if (selectedPartId !== 0) return
    selectPart(rows[0].partId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parts.content])

  const selectPart = (partId) => {
    setSelectedPartId(partId)
    setMountedComponentsExpanded(false)
    setTrack({ status: 'loading', data: {} })
    // Only the last selection may write: A then B must not leave B selected with the track of A.
    const token = ++trackRequest.current
    fetchPartTrack(partId)
      .then((data) => { if (token === trackRequest.current) setTrack({ status: 'ready', data }) })
      .catch(() => { if (token === trackRequest.current) setTrack({ status: 'error', data: {} }) })
  }

  const closeTrack = () => {
    trackRequest.current++
    setSelectedPartId(0)
    setTrack({ status: 'idle', data: {} })
  }

  // El total REAL de canceladas de la orden, del `totalElements` que sirve Sun — no de las filas
  // cargadas. Si el sondeo no se pudo leer, se cae al número de filas antes que inventar un guion:
  // decir «17 de —» sería peor que decir «17 de 17» cuando 17 es lo único que se sabe.
  const cancelledProbe = partCounts.byGroup.CANCELLED
  const cancelledProbeUnreadable = cancelledProbe === '' || cancelledProbe === undefined
  // Once the cancelled list has loaded, its own (search-filtered) total drives the label and pager.
  const cancelledTotal = cancelledParts.loaded || cancelledProbeUnreadable
    ? cancelledParts.totalElements
    : cancelledProbe

  // E9: the ids of ALL the parts of the order (not only the loaded page), cancelled ones left out.
  const handleOpenBatchPrint = () => setShowBatchPrint(true)
  const handleCloseBatchPrint = () => setShowBatchPrint(false)
  const loadAllPartIds = useCallback(async () => {
    // Page through the whole order: one page of 500 left out the parts of a bigger order.
    const partIds = []
    let total = 1
    for (let page = 0; partIds.length < total; page++) {
      const next = await fetchParts(fabricationOrderId, { page, size: PRINT_PAGE_SIZE, excludeCancelled: true })
      if (next.content.length === 0) break
      partIds.push(...next.content.map((part) => part.partId))
      total = next.totalElements
    }
    return partIds
  }, [fabricationOrderId])

  return (
    <Box data-testid='parts-block'>
      {showBatchPrint && (
        <PartsBatchPrintDialog
          open
          onClose={handleCloseBatchPrint}
          partIds={loadAllPartIds}
          columns={PART_QR_COLUMNS.map(c => ({ field: c.field, name: t_(c.name) }))}
        />
      )}

      <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 2.5 }} flexWrap='wrap' useFlexGap>
        {/* §5.4 — la pieza elegida se nombra EN LA BARRA, junto a las acciones que operan sobre
            ella. La tarjeta «Pieza seleccionada» queda abajo y se va de pantalla en cuanto el
            recorrido crece; el botón «Abrir en planta» de al lado, en cambio, actúa sobre esta
            pieza y necesita decir sobre cuál. */}
        {!!track.data.part?.code && (
          <Chip
            data-testid='parts-selected-part'
            icon={<CheckIcon />}
            label={track.data.part.code}
            sx={{
              fontWeight: 700,
              color: 'info.main',
              backgroundColor: alpha(theme.palette.info.main, 0.12),
              border: `1px solid ${alpha(theme.palette.info.main, 0.32)}`,
              '& .MuiChip-icon': { color: 'info.main' }
            }}
          />
        )}
        {/* Historia 226, E9 (S203): the labels of every part of the order, in one go */}
        <Button data-testid='parts-print-labels' size='small' variant='outlined' onClick={handleOpenBatchPrint}>
          {t_('Print the labels of the parts')}
        </Button>
        <TextField
          data-testid='parts-search-input'
          size='small'
          label={t_('Search by code')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <TextField
          select
          size='small'
          data-testid='parts-lifecycle-filter'
          label={t_('Status group')}
          value={lifecycleGroup}
          onChange={(event) => setLifecycleGroup(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem data-testid='parts-lifecycle-option-ALL' value=''>{t_('All')}</MenuItem>
          {/* Cancelled is deliberately NOT an option (30/09, PO): it has its own button and its own
              request below the grid, and the main list is fetched without cancelled parts.
              ⚠️ LAS OPCIONES SALEN DEL ENUM, no de cinco líneas escritas a mano. Eran cuatro
              literales, y el quinto grupo de Sun (S416) no habría aparecido aquí por no estar
              escrito — que es exactamente el modo en que un filtro pierde un valor sin que nada
              falle: la opción simplemente no existe y nadie la echa de menos. */}
          {GROUP_ORDER.map((group) => (
            <MenuItem key={group} data-testid={`parts-lifecycle-option-${group}`} value={group}>
              {partLifecycleGroupLabel(group, t_)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* §5.4 — el recuento de la barra: «n piezas en esta orden». Sale del total del servidor,
          no de las filas que se ven. */}
      {partCounts.read && partCounts.total !== 0 && (
        <Stack direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2, mt: -1 }} flexWrap='wrap' useFlexGap>
          <Typography data-testid='parts-total-count' variant='body2' sx={{ color: 'text.secondary' }}>
            {t_('{{n}} parts in this order', { n: partCountText(partCounts.total) })}
          </Typography>
          {/* El desglose por grupo. NO es la leyenda-atajo que S388 retiró —no filtra nada, no se
              pulsa— sino lo que el desplegable no puede decir: cuántas hay de cada grupo. Y cada
              grupo sale SIEMPRE, también a cero: un cero dicho es información, y un grupo que
              desaparece se lee como «ese estado no existe en esta orden». */}
          <Stack data-testid='parts-group-counts' direction='row' spacing={1.5} flexWrap='wrap' useFlexGap>
            {PART_LIFECYCLE_GROUPS.map((group) => (
              <Typography
                key={group}
                data-testid={`parts-group-count-${group}`}
                variant='body2'
                sx={{ color: 'text.secondary' }}
              >
                <Box component='span' sx={{ fontWeight: 800, color: groupColor(theme, group) }}>
                  {partCountText(partCounts.byGroup[group])}
                </Box>
                {/* El espacio es un nodo de texto de verdad, no un margen: así «19 Sin empezar» se
                    lee igual en pantalla que en cualquier extracción de texto de la página. */}
                {' '}
                {partLifecycleGroupLabel(group, t_)}
              </Typography>
            ))}
          </Stack>
        </Stack>
      )}

      {/* ⚠️ AQUÍ HABÍA TRES CHIPS (Finalizada · En curso · Rechazada) que hacían de leyenda y de
          atajo al desplegable de al lado. El PO los retira, igual que los del bloque de Workflow.
          ⚠️ PERO NO ERAN LO MISMO QUE AQUÉLLOS, y conviene que quede escrito: los de Workflow eran
          una leyenda convertida en filtro DE CLIENTE; éstos escribían en el MISMO `lifecycleGroup`
          que el desplegable "Grupo de estado" de arriba, y ese valor VIAJA AL SERVIDOR en una
          consulta paginada (RN70/D20/P-7). Eran un atajo, no un filtro paralelo — por eso
          quitarlos no pierde ninguna capacidad: el desplegable hace exactamente lo mismo y está a
          diez píxeles. Quitar el DESPLEGABLE sí sería una pérdida, y por eso se queda.
          📌 Y una asimetría que desaparece con ellos: eran TRES chips frente a CINCO opciones del
          desplegable — `CANCELLED` se podía filtrar y no tenía chip, el mismo agujero que tenía la
          leyenda de Workflow antes de S449. Al irse los chips, se cierra solo. */}

      {parts.content.length === 0 && partsStatus === 'loading' && (
        <Typography data-testid='parts-loading' variant='body2' sx={{ color: 'text.secondary' }}>{t_('Loading...')}</Typography>
      )}
      {parts.content.length === 0 && partsStatus === 'error' && (
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Typography data-testid='parts-error' variant='body2' sx={{ color: 'error.main' }}>{t_('Could not load the parts')}</Typography>
          <Button data-testid='parts-retry' size='small' variant='outlined' onClick={loadParts} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS }}>{t_('Retry')}</Button>
        </Stack>
      )}
      {parts.content.length === 0 && partsStatus === 'ready' && (
        <Typography data-testid='parts-empty' variant='body2' sx={{ color: 'text.secondary' }}>
          {debouncedSearch || lifecycleGroup
            ? t_('No parts match')
            : (partCounts.read && Number(partCounts.total) > 0 ? t_('No active parts') : t_('This order has no parts'))}
        </Typography>
      )}

      {parts.content.length > 0 && (
        <>
          {/* Pasada 20 (defecto A, mockup 06-piezas.png): only the grid and the selected part's
              identity/QR live side by side — the sheet, mounted components and track are NOT
              nested inside the narrow right card any more; they run full width below, so they get
              real room instead of being squeezed into ~340px.
              ⚠️⚠️ S460 (PO, 23/09) — LAS DOS TARJETAS ACABABAN «A SU AIRE»: cada una con su propia
              altura y sin bordes alineados, porque `alignItems: 'flex-start'` deja que cada Box
              mida solo lo que su contenido pide. `stretch` iguala las dos a la altura de la fila. */}
          <Stack data-testid='parts-cards-row' direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems='stretch'>
            <PartsGrid
              parts={parts}
              selectedPartId={selectedPartId}
              onSelectPart={selectPart}
              shownParts={shownParts}
              hasMoreParts={hasMoreParts}
              loadingMore={loadingMore}
              loadMoreFailed={loadMoreFailed}
              onLoadMore={loadMoreParts}
            />
            <SelectedPartCard selectedPartId={selectedPartId} track={track} />
          </Stack>

          {!!selectedPartId && track.status === 'ready' && (
            <PartTrackPanel
              track={track}
              mountedComponentsExpanded={mountedComponentsExpanded}
              setMountedComponentsExpanded={setMountedComponentsExpanded}
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              onClose={closeTrack}
            />
          )}
        </>
      )}

      {(cancelledProbeUnreadable ? partCounts.read : Number(cancelledProbe) > 0) && (
        <CancelledPartsSection
          cancelledParts={cancelledParts}
          cancelledTotal={cancelledTotal}
          expanded={cancelledExpanded}
          onToggle={() => setCancelledExpanded((prev) => !prev)}
          loadingMore={loadingMoreCancelled}
          onLoadMore={() => loadCancelledParts(cancelledParts.page + 1)}
          selectedPartId={selectedPartId}
          onSelectPart={selectPart}
        />
      )}
    </Box>
  )
}

export default PartsBlock
